/**
 * Enrutado de la SPA.
 *
 * La **raíz hace de callback**: cuando se vuelve de Google llega con `?code=`, y `supabase-js` lo
 * canjea solo al cargar. No hay una ruta `/acceso/callback` porque el allow-list de GoTrue solo
 * permite el origen desnudo y compara con coincidencia exacta; añadir una ruta obligaría a
 * reiniciar el GoTrue que comparten otros cuatro proyectos en producción.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { useSession } from '../auth/session.js';
import { PantallaAcceso } from './acceso.js';
import { PanelAdmin, PanelAlumno, PanelProfesor } from './paneles.js';
import { PantallaUsuarios } from './admin-usuarios.js';
import { PantallaAsignaturas } from './asignaturas.js';
import { PantallaCuestionarios } from './cuestionarios.js';
import { PantallaEditor } from './editor.js';
import { PantallaImportar } from './importar.js';
import { PantallaEntrar } from './join.js';
import { PantallaJugar } from './play.js';
import { PantallaSala } from './host.js';
import { PantallaAnaliticas } from './analiticas.js';
import { PantallaCuenta } from './cuenta.js';
import { PantallaPrivacidad } from './privacidad.js';
import { Aviso } from '../components/sistema/aviso.js';
import { Boton } from '../components/sistema/boton.js';
import { Marca } from '../components/sistema/marca.js';
import { ProveedorAvisos } from '../components/sistema/avisos.js';

/** Una dirección que no lleva a ninguna parte. */
function NoExiste(): React.JSX.Element {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = `${t('common.notFoundTitle')} · ${t('app.name')}`;
  }, [t]);

  return (
    <main
      id="contenido"
      className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-6 p-6 text-center"
    >
      <Marca size={72} className="text-edge" />
      <div>
        <h1 className="font-display text-h2 font-extrabold">{t('common.notFoundTitle')}</h1>
        <p className="text-body text-text-dim mt-2">{t('common.notFoundLead')}</p>
        {/* La dirección se enseña: quien llega por un enlace mal copiado necesita verla para
            saber qué se rompió. */}
        <p className="text-micro text-text-dim mt-3 break-all">{window.location.pathname}</p>
      </div>
      <Boton variante="primaria" onClick={() => window.location.assign('/')}>
        {t('common.backHome')}
      </Boton>
    </main>
  );
}

/**
 * Pantalla de último recurso.
 *
 * Existe porque quedarse en «Cargando…» para siempre es el peor fallo posible: la persona no
 * sabe si esperar, si recargar o si el problema es suyo.
 */
function Fallo(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <main
      id="contenido"
      className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-6 p-6"
    >
      <Marca size={72} className="text-edge" />
      <Aviso tono="error" interrumpe className="w-full">
        <p className="font-display font-bold">{t('common.failedTitle')}</p>
        <p className="text-body text-text-dim mt-1">{t('common.failedLead')}</p>
      </Aviso>
      <Boton variante="primaria" onClick={() => window.location.reload()}>
        {t('common.reload')}
      </Boton>
    </main>
  );
}

/**
 * La espera del arranque.
 *
 * Lleva la marca y no solo la palabra «Cargando…»: es lo primero que ve todo el mundo, incluida
 * la vuelta de Google, y una frase suelta sobre el fondo no dice si la aplicación está viva.
 */
function Cargando(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <main id="contenido" className="flex min-h-dvh flex-col items-center justify-center gap-5 p-6">
      <Marca size={72} className="text-edge" />
      <p role="status" className="text-micro font-display text-text-dim font-bold uppercase">
        {t('common.loading')}
      </p>
    </main>
  );
}

/**
 * Puerta de entrada de toda ruta protegida.
 *
 * Mientras la sesión se restaura muestra un estado de carga: sin él, cada recarga daría un
 * parpadeo de pantalla de acceso a quien sí tiene sesión.
 */
function Protegida({
  children,
}: {
  children: (profile: NonNullable<ReturnType<typeof useProfile>>) => React.JSX.Element;
}): React.JSX.Element {
  const state = useSession((store) => store.state);

  if (state.status === 'loading') return <Cargando />;
  if (state.status === 'failed') return <Fallo />;
  if (state.status !== 'active') return <Navigate to="/" replace />;
  return children(state.profile);
}

function useProfile() {
  const state = useSession((store) => store.state);
  return state.status === 'active' ? state.profile : null;
}

/** La raíz: pantalla de acceso, callback de Google y reparto por rol, todo en una. */
function Raiz(): React.JSX.Element {
  const state = useSession((store) => store.state);

  if (state.status === 'loading') return <Cargando />;
  if (state.status === 'failed') return <Fallo />;
  if (state.status !== 'active') return <PantallaAcceso />;

  switch (state.profile.role) {
    case 'admin':
      return <PanelAdmin profile={state.profile} />;
    case 'teacher':
      return <PanelProfesor profile={state.profile} />;
    case 'student':
      return <PanelAlumno profile={state.profile} />;
  }
}

const router = createBrowserRouter([
  { path: '/', element: <Raiz /> },
  {
    path: '/admin/usuarios',
    element: (
      <Protegida>
        {(profile) =>
          // La comprobación de la interfaz es comodidad, no seguridad: quien escriba la ruta a
          // mano o fabrique la petición es rechazado por el servidor de todos modos.
          profile.role === 'admin' ? (
            <PantallaUsuarios profile={profile} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      </Protegida>
    ),
  },
  // El material del profesor. Quien administra también puede editar el suyo propio.
  {
    path: '/asignaturas',
    element: (
      <Protegida>
        {(profile) =>
          profile.role === 'student' ? (
            <Navigate to="/" replace />
          ) : (
            <PantallaAsignaturas profile={profile} />
          )
        }
      </Protegida>
    ),
  },
  {
    path: '/asignaturas/:subjectId',
    element: (
      <Protegida>
        {(profile) =>
          profile.role === 'student' ? (
            <Navigate to="/" replace />
          ) : (
            <PantallaCuestionarios profile={profile} />
          )
        }
      </Protegida>
    ),
  },
  {
    path: '/asignaturas/:subjectId/importar',
    element: (
      <Protegida>
        {(profile) =>
          profile.role === 'student' ? (
            <Navigate to="/" replace />
          ) : (
            <PantallaImportar profile={profile} />
          )
        }
      </Protegida>
    ),
  },
  {
    path: '/cuestionarios/:quizId',
    element: (
      <Protegida>
        {(profile) =>
          profile.role === 'student' ? (
            <Navigate to="/" replace />
          ) : (
            <PantallaEditor profile={profile} />
          )
        }
      </Protegida>
    ),
  },
  // La cuenta y la privacidad. La de privacidad **no exige sesión**: se enlaza desde la pantalla
  // de acceso, y exigir entrar para leer qué datos se guardan sería justo al revés.
  { path: '/privacidad', element: <PantallaPrivacidad /> },
  {
    path: '/cuenta',
    element: <Protegida>{(profile) => <PantallaCuenta profile={profile} />}</Protegida>,
  },
  // Las analíticas. Son datos de menores identificables uno a uno: la comprobación de rol aquí es
  // comodidad, y quien decide de verdad son las políticas de la base de datos.
  {
    path: '/partidas/:gameId',
    element: (
      <Protegida>
        {(profile) =>
          profile.role === 'student' ? (
            <Navigate to="/" replace />
          ) : (
            <PantallaAnaliticas profile={profile} />
          )
        }
      </Protegida>
    ),
  },
  // El bucle de juego. `/join` es la puerta por la que entra el alumno con el código proyectado;
  // las otras dos exigen sesión porque el socket rechaza a quien no tiene perfil en Clica.
  {
    path: '/join',
    element: <Protegida>{(profile) => <PantallaEntrar profile={profile} />}</Protegida>,
  },
  {
    path: '/play/:pin',
    element: <Protegida>{(profile) => <PantallaJugar profile={profile} />}</Protegida>,
  },
  {
    path: '/host/:pin',
    element: (
      <Protegida>
        {(profile) =>
          profile.role === 'student' ? (
            <Navigate to="/" replace />
          ) : (
            <PantallaSala profile={profile} />
          )
        }
      </Protegida>
    ),
  },
  // Cualquier ruta desconocida vuelve a la raíz, que ya sabe qué mostrar según haya sesión o no.
  // Una dirección que no existe explica qué pasa y conserva la URL en la barra. Antes se
  // redirigía a la raíz con `replace`, que además borraba del historial la dirección rota: quien
  // llegaba por un enlace mal copiado no podía ni ver qué había escrito.
  { path: '*', element: <NoExiste /> },
]);

export function App(): React.JSX.Element {
  return (
    <ProveedorAvisos>
      <RouterProvider router={router} />
    </ProveedorAvisos>
  );
}
